import { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import type { ZoomSyncErrorBody } from "services/appointments";
import {
  appointmentsApi,
  useLazyGetAppointmentQuery,
  useZoomSyncAppointmentMutation
} from "services/appointments";

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 10;

export function useZoomLinkGeneration(appointmentId: number | undefined) {
  const dispatch = useAppDispatch();
  const [zoomSyncAppointment, { isLoading: isZoomSyncing }] =
    useZoomSyncAppointmentMutation();
  const [fetchAppointment] = useLazyGetAppointmentQuery();
  const [isPollingZoom, setIsPollingZoom] = useState(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Every outcome of this button used to be silent: a rejection was swallowed here, and a poll
  // that ran out just stopped the spinner. Staff reported "nothing happens" and had to open Dev
  // Tools to find the response. Both endings now say what happened and what to do about it.
  const announce = useCallback(
    (message: string) => {
      dispatch(setCustomMessage(message));
      dispatch(setOpen(true));
    },
    [dispatch]
  );

  const handleGenerateZoomLink = useCallback(async () => {
    if (!appointmentId) return;
    try {
      await zoomSyncAppointment(appointmentId).unwrap();
      setIsPollingZoom(true);
      let attempts = 0;
      const stopPolling = () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setIsPollingZoom(false);
      };

      pollIntervalRef.current = setInterval(async () => {
        attempts++;
        const ranOut = attempts >= MAX_POLL_ATTEMPTS;
        let joinUrl: string | undefined;

        try {
          joinUrl = (await fetchAppointment({ id: appointmentId }).unwrap())
            ?.zoom_join_url;
        } catch (err) {
          // A transient read failure must not strand the poll. Without this the rejected promise
          // escapes the interval callback unhandled, the interval is never cleared, and the
          // button spins until the component unmounts. Keep polling; `ranOut` still ends it.
          console.error("Could not re-read the appointment while polling", err);
        }

        if (joinUrl || ranOut) {
          stopPolling();
          if (joinUrl) {
            // ENG-2087: the read above fills only the `{ id }` cache entry. The callers show the
            // link from other copies — the appointment page reads `{ id, query }`, the scheduler
            // grid reads `Events` — so without this the link was found and never displayed.
            dispatch(
              appointmentsApi.util.invalidateTags([
                { type: "Appointments", id: appointmentId },
                "Events"
              ])
            );
          } else {
            // The sync request was accepted, so Zoom IS connected for this clinic — the meeting
            // itself could not be created. Retrying is the right advice; sending someone to
            // Settings to reconnect a working connection is not.
            announce(
              "Zoom hasn't returned a link yet. Please try again in a moment."
            );
          }
        }
      }, POLL_INTERVAL_MS);
    } catch (err) {
      // ZOOM_DISABLED / ZOOM_NOT_CONNECTED both carry a message naming the fix, and both are
      // resolved in Settings rather than by pressing the button again.
      const body = (err as { data?: ZoomSyncErrorBody })?.data;
      announce(
        body?.message ??
          "Couldn't generate the Zoom link. Please try again, or check your Zoom connection in Settings."
      );
    }
  }, [
    appointmentId,
    zoomSyncAppointment,
    fetchAppointment,
    announce,
    dispatch
  ]);

  return {
    handleGenerateZoomLink,
    isZoomGenerating: isZoomSyncing || isPollingZoom
  };
}
